import React, { useState, useEffect } from "react";
import { leaveAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Plus, CalendarDays, Loader2 } from "lucide-react";

const LeavePoliciesPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    leave_type: "",
    annual_quota: 12,
    carry_forward: false,
    max_carry_forward: 0,
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await leaveAPI.getPolicies();
      setPolicies(response.data);
    } catch (error) {
      toast.error("Failed to fetch policies");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await leaveAPI.createPolicy(formData);
      toast.success("Policy created successfully");
      setShowCreateDialog(false);
      setFormData({
        name: "",
        leave_type: "",
        annual_quota: 12,
        carry_forward: false,
        max_carry_forward: 0,
      });
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create policy");
    } finally {
      setCreateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-policies-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Leave Policies</h1>
          <p className="text-slate-500">Configure leave types and quotas</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-policy-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Policy
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Create Leave Policy</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="form-group">
                <Label>Policy Name</Label>
                <Input
                  placeholder="Annual Casual Leave"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="policy-name-input"
                />
              </div>
              <div className="form-group">
                <Label>Leave Type</Label>
                <Input
                  placeholder="casual"
                  value={formData.leave_type}
                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value.toLowerCase() })}
                  required
                  data-testid="policy-type-input"
                />
              </div>
              <div className="form-group">
                <Label>Annual Quota (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.annual_quota}
                  onChange={(e) => setFormData({ ...formData, annual_quota: parseInt(e.target.value) })}
                  required
                  data-testid="policy-quota-input"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="carry_forward"
                  checked={formData.carry_forward}
                  onCheckedChange={(checked) => setFormData({ ...formData, carry_forward: checked })}
                />
                <Label htmlFor="carry_forward" className="text-sm font-normal">
                  Allow carry forward
                </Label>
              </div>
              {formData.carry_forward && (
                <div className="form-group">
                  <Label>Max Carry Forward (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.max_carry_forward}
                    onChange={(e) => setFormData({ ...formData, max_carry_forward: parseInt(e.target.value) })}
                  />
                </div>
              )}
              <Button type="submit" className="w-full btn-primary" disabled={createLoading} data-testid="submit-policy">
                {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Policy"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Policies Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy Name</TableHead>
                <TableHead>Leave Type</TableHead>
                <TableHead>Annual Quota</TableHead>
                <TableHead>Carry Forward</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No policies found. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                policies.map((policy) => (
                  <TableRow key={policy.id} data-testid={`policy-row-${policy.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <CalendarDays className="w-5 h-5 text-indigo-600" />
                        </div>
                        <span className="font-medium">{policy.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{policy.leave_type}</Badge>
                    </TableCell>
                    <TableCell>{policy.annual_quota} days</TableCell>
                    <TableCell>
                      {policy.carry_forward ? (
                        <Badge variant="default">Up to {policy.max_carry_forward} days</Badge>
                      ) : (
                        <Badge variant="secondary">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(policy.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeavePoliciesPage;
